import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { type AlertCatalogView, type AlertCenterView, type IndividualAlertView, type PlatformAlertView } from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import { formatMoney } from '@vello/format';
import { Badge, Button, EmptyState, Field, FilterBar, FilterBarNote, ListTable, Notice, PageHeader, Panel, Select, TextArea, TextInput, type Column, SectionTitle, Text, Row, Stack, Measure, AUTO_REFRESH_MS, useAutoRefresh } from '@vello/web-theme';
import {
  alertAmountMinor,
  alertCenterAuthority,
  alertSubjectRoute,
  alertTone,
  debtAlertParams,
  riderDebtAuthority,
  formatMarketInstant,
  marketTimeZone,
  tailOf,
  zonedLocalToUtc,
} from '../../src/index';
import { ScopeMarketNotice, useScopeMarket } from '../components/scope-market';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';

export function AlertCenterScreen(): ReactElement {
  const { runtime, markets, grants } = useAdmin();
  const { navigate } = useRouter();
  const { t, lang } = useT();
  const { v, fail, reason, refusal: refusalOf } = useVocab();
  const authority = alertCenterAuthority(grants);
  // D536: a "debt settled" notice carries an amount and a debt reference, never a person; the personal word turns the reference into the rider.
  const { mayPersonal } = riderDebtAuthority(grants);

  const { market, choices } = useScopeMarket();
  const timeZone = marketTimeZone(market, markets);

  const [center, setCenter] = useState<AlertCenterView | undefined>(undefined);
  const [catalog, setCatalog] = useState<AlertCatalogView | undefined>(undefined);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [notice, setNotice] = useState<{ readonly tone: 'success' | 'danger'; readonly text: string } | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ userId: '', title: '', body: '', from: '', to: '' });
  const [listed, setListed] = useState<readonly IndividualAlertView[] | undefined>(undefined);
  const readSeq = useRef(0);

  const refuse = useCallback(
    (reasonCode: string | undefined, fallback: string, status?: number): string => {
      const text = refusalOf('refusal.admin.risk_alert', { code: fallback, reasonCode, status });
      return text ?? t('lmap.err.generic', { code: fail({ code: fallback, reasonCode }) });
    },
    [t, fail, refusalOf],
  );

  const load = useCallback(async (): Promise<void> => {
    const seq = ++readSeq.current;
    if (market === undefined) {
      setCenter(undefined);
      setCatalog(undefined);
      setError(undefined);
      return;
    }
    const [c, cat] = await Promise.all([runtime.ops.alertCenter(market, unreadOnly), runtime.ops.alertCatalog(market)]);
    if (seq !== readSeq.current) return;
    if (c.ok) {
      setCenter(c.value);
      setError(undefined);
    } else {
      // A refused read keeps the center unknown: the screen says it could not load, never "no alerts".
      setError(refuse(c.error.reasonCode, c.error.code, c.error.status));
    }
    if (cat.ok) setCatalog(cat.value);
  }, [runtime, market, unreadOnly, refuse]);

  useEffect(() => {
    setCenter(undefined);
    void load();
  }, [load]);

  useAutoRefresh(load, { intervalMs: AUTO_REFRESH_MS.queue, enabled: market !== undefined });

  const markRead = async (alert: PlatformAlertView): Promise<void> => {
    if (market === undefined) return;
    setBusy(true);
    const res = await runtime.ops.markAlertRead(market, alert.alertId);
    setBusy(false);
    if (!res.ok) setNotice({ tone: 'danger', text: refuse(res.error.reasonCode, res.error.code, res.error.status) });
    await load();
  };

  const money = useCallback(
    (alert: PlatformAlertView, key: string): string => {
      const minor = alertAmountMinor(alert, key);
      const currency = alert.params.currency;
      return minor !== undefined && typeof currency === 'string' && isIso4217Currency(currency)
        ? formatMoney({ amountMinor: minor, currency }, { locale: lang, timeZone: 'UTC', measurement: 'metric' })
        : `${String(alert.params[key] ?? '')} ${String(currency ?? '')}`.trim();
    },
    [lang],
  );

  /** What the alert says, from its type and its render parameters (no stored free text, so every language reads it). */
  const message = (a: PlatformAlertView): string => {
    switch (a.type) {
      case 'HIGH_ESTIMATED_FARE':
      case 'HIGH_FINAL_FARE':
        return t('alert.msg.amount', { amount: money(a, 'amountMinor'), limit: money(a, 'thresholdMinor') });
      case 'RIDER_DEBT_SETTLED': {
        const p = debtAlertParams(a.params);
        return t('alert.msg.debtSettled', { amount: money(a, 'amountMinor'), origin: p.origin !== undefined ? v('debt.origin', p.origin) : '—', payments: String(p.payments ?? 0) });
      }
      case 'RIDE_IN_RISK_AREA':
      {
        const where = reason(String(a.params.riskReason ?? 'RISK_AREA_AT_PICKUP'));
        const area = String(a.params.description ?? '');
        return area === '' ? where : `${where} ${area}`;
      }
      default:
        return v('alerts.platform_alert_type', a.type);
    }
  };

  const createIndividual = async (): Promise<void> => {
    if (market === undefined) return;
    const tz = timeZone ?? 'UTC';
    const startsAt = form.from.trim() === '' ? undefined : zonedLocalToUtc(form.from, tz);
    const expiresAt = form.to.trim() === '' ? undefined : zonedLocalToUtc(form.to, tz);
    if ((form.from.trim() !== '' && startsAt === undefined) || (form.to.trim() !== '' && expiresAt === undefined)) {
      setNotice({ tone: 'danger', text: t('alert.err.window') });
      return;
    }
    setBusy(true);
    setNotice(undefined);
    const res = await runtime.ops.createIndividualAlert({ market, userId: form.userId.trim(), title: form.title.trim(), body: form.body.trim(), ...(startsAt !== undefined ? { startsAt } : {}), ...(expiresAt !== undefined ? { expiresAt } : {}) });
    setBusy(false);
    if (res.ok) {
      setNotice({ tone: 'success', text: t('alert.done.created', { value: formatMarketInstant(res.value.alert.expiresAt, timeZone, lang) }) });
      const userId = form.userId.trim();
      setForm((f) => ({ ...f, title: '', body: '', from: '', to: '' }));
      const l = await runtime.ops.individualAlerts(userId);
      setListed(l.ok ? l.value.alerts : undefined);
    } else setNotice({ tone: 'danger', text: refuse(res.error.reasonCode, res.error.code, res.error.status) });
  };

  const columns: readonly Column<PlatformAlertView>[] = [
    { key: 'when', header: t('alert.col.starts'), width: 'minmax(150px, 1fr)', render: (a) => <Text role="caption" tone="secondary" mono>{formatMarketInstant(a.startsAt, timeZone, lang)}</Text> },
    { key: 'type', header: t('alert.col.type'), width: 'minmax(190px, 1.2fr)', render: (a) => <Badge tone={alertTone(a.type)}>{v('alerts.platform_alert_type', a.type)}</Badge> },
    { key: 'msg', header: t('alert.col.message'), width: 'minmax(260px, 2fr)', render: (a) => <span className="tnum">{message(a)}</span> },
    {
      key: 'subject',
      header: t('alert.col.subject'),
      width: 'minmax(120px, 0.8fr)',
      render: (a) => {
        const target = alertSubjectRoute(a);
        if (a.type === 'RIDER_DEBT_SETTLED' && mayPersonal && a.subjectRef !== undefined && market !== undefined) {
          const ref = a.subjectRef;
          return <Button variant="ghost" onClick={() => navigate('finance_rider_debts', { debt: ref, market })}>{t('alert.action.debtOwner')}</Button>;
        }
        return target !== undefined ? <Button variant="ghost" onClick={() => navigate('ride_detail', { id: target.rideId })}><span className="tnum">{tailOf(target.rideId)}</span></Button> : <Text role="caption" tone="secondary">—</Text>;
      },
    },
    { key: 'expires', header: t('alert.col.expires'), width: 'minmax(150px, 1fr)', render: (a) => <Text role="caption" tone="secondary" mono>{formatMarketInstant(a.expiresAt, timeZone, lang)}</Text> },
    {
      key: 'act',
      header: '',
      width: 'minmax(150px, 0.9fr)',
      render: (a) =>
        a.readAt !== undefined ? <Badge tone="neutral">{t('alert.read')}</Badge> : authority.mayManage ? <Button variant="secondary" disabled={busy} onClick={() => void markRead(a)}>{t('alert.action.markRead')}</Button> : <Badge tone="warning">{t('alert.unread')}</Badge>,
    },
  ];

  return (
    <>
      <PageHeader title={t('alert.title')} subtitle={t('alert.subtitle')} />

      {error !== undefined && <div><Notice tone="danger">{error}</Notice></div>}
      {notice !== undefined && <div><Notice tone={notice.tone}>{notice.text}</Notice></div>}

      {market === undefined ? (
        <Panel>
          <ScopeMarketNotice choices={choices} />
        </Panel>
      ) : (
        <>
          <Panel>
            <FilterBar gap={5}>
              <FilterBarNote><span className="tnum"><strong>{center?.unread ?? 0}</strong> <Text role="caption" tone="secondary">{t('alert.unreadCount')}</Text></span></FilterBarNote>
              <FilterBarNote><Text role="caption" tone="secondary">{t('alert.limit', { value: center?.limit ?? 0 })}</Text></FilterBarNote>
              <div className="vello-admin-a-field">
                <Field label={t('alert.filter')} htmlFor="alert-filter">
                  <Select id="alert-filter" value={unreadOnly ? 'unread' : 'all'} onChange={(e) => setUnreadOnly(e.target.value === 'unread')}>
                    <option value="all">{t('alert.filter.all')}</option>
                    <option value="unread">{t('alert.filter.unread')}</option>
                  </Select>
                </Field>
              </div>
            </FilterBar>
          </Panel>

          {/* D854 (LT-011): without published limits the four amount alerts never fire; an empty centre must not read as "all quiet". */}
          {catalog !== undefined && catalog.thresholds.value === undefined && (
            <div><Notice tone="warning">{t('alert.thresholds.off')}</Notice></div>
          )}

          <ListTable
            columns={columns}
            rows={center?.alerts ?? []}
            rowKey={(a) => a.alertId}
            loading={center === undefined && error === undefined}
            loadingLabel={t('ui.loading')}
            error={error !== undefined ? t('alert.empty.failed') : undefined}
            onRetry={() => void load()}
            retryLabel={t('ui.retry')}
            empty={<EmptyState title={t('alert.empty.title')} detail={t('alert.empty.detail')} />}
            minWidth={1000}
          />

          {catalog !== undefined && (
            <Panel><Stack gap={4}>
              <SectionTitle>{t('alert.catalog.title')}</SectionTitle>
              <Text as="p" role="caption" tone="secondary">{t('alert.catalog.hint', { hours: catalog.catalog.defaultValidityHours, limit: catalog.catalog.centerLimit, version: catalog.catalog.policyVersion })}</Text>
              <Text as="p" role="caption" tone="secondary" mono>
                {catalog.thresholds.value !== undefined
                  ? t('alert.thresholds.set', { version: catalog.thresholds.policyVersion, currency: catalog.thresholds.value.currency })
                  : t('alert.thresholds.unset')}
              </Text>
              <ListTable
                columns={[
                  { key: 'type', header: t('alert.col.type'), width: 'minmax(240px, 2fr)', render: (r: AlertCatalogView['types'][number]) => <strong>{v('alerts.platform_alert_type', r.type)}</strong> },
                  { key: 'on', header: t('alert.catalog.active'), width: 'minmax(110px, 0.8fr)', render: (r) => <Badge tone={r.enabled ? 'success' : 'neutral'}>{t(r.enabled ? 'alert.catalog.on' : 'alert.catalog.off')}</Badge> },
                  { key: 'to', header: t('alert.catalog.recipient'), width: 'minmax(130px, 1fr)', render: (r) => v('alerts.alert_recipient', r.recipient) },
                  { key: 'src', header: t('alert.catalog.producer'), width: 'minmax(170px, 1fr)', render: (r) => <Text role="caption" tone="secondary">{t(r.producer === 'WIRED' ? 'alert.catalog.wired' : 'alert.catalog.planned')}</Text> },
                ]}
                rows={catalog.types}
                rowKey={(r) => r.type}
                empty={<EmptyState title={t('alert.empty.title')} />}
                minWidth={700}
              />
            </Stack></Panel>
          )}

          {authority.mayWrite && (
            <Panel><Stack gap={5}>
              <SectionTitle>{t('alert.individual.title')}</SectionTitle>
              <Text as="p" role="caption" tone="secondary">{t('alert.individual.hint', { hours: catalog?.catalog.defaultValidityHours ?? 24 })}</Text>
              <Measure size="lg" gap={4}>
                <Field label={t('alert.individual.user')} htmlFor="alert-user" hint={t('alert.individual.userHint')}>
                  <TextInput id="alert-user" className="tnum" value={form.userId} onChange={(e) => setForm({ ...form, userId: e.target.value })} />
                </Field>
                <Field label={t('alert.individual.titleField')} htmlFor="alert-title">
                  <TextInput id="alert-title" value={form.title} maxLength={120} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                </Field>
                <Field label={t('alert.individual.body')} htmlFor="alert-body">
                  <TextArea id="alert-body" value={form.body} maxLength={1000} onChange={(e) => setForm({ ...form, body: e.target.value })} />
                </Field>
                <Row gap={4} align="stretch" wrap>
                  <Field label={t('alert.individual.from')} htmlFor="alert-from" hint={t('alert.individual.fromHint', { value: timeZone ?? 'UTC' })}>
                    <TextInput id="alert-from" className="tnum" type="datetime-local" value={form.from} onChange={(e) => setForm({ ...form, from: e.target.value })} />
                  </Field>
                  <Field label={t('alert.individual.to')} htmlFor="alert-to" hint={t('alert.individual.toHint')}>
                    <TextInput id="alert-to" className="tnum" type="datetime-local" value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} />
                  </Field>
                </Row>
                <Row gap={3} align="stretch">
                  <Button variant="primary" disabled={busy || form.userId.trim() === '' || form.title.trim() === '' || form.body.trim() === ''} onClick={() => void createIndividual()}>{t('alert.individual.send')}</Button>
                  <Button variant="ghost" disabled={busy || form.userId.trim() === ''} onClick={() => void runtime.ops.individualAlerts(form.userId.trim()).then((r) => setListed(r.ok ? r.value.alerts : []))}>{t('alert.individual.list')}</Button>
                </Row>
              </Measure>
              {listed !== undefined && (
                <div>
                  <ListTable
                    columns={[
                      { key: 'title', header: t('alert.individual.titleField'), width: 'minmax(200px, 1.5fr)', render: (a: IndividualAlertView) => <strong>{a.title ?? '—'}</strong> },
                      { key: 'from', header: t('alert.col.starts'), width: 'minmax(150px, 1fr)', render: (a) => <Text role="caption" tone="secondary" mono>{formatMarketInstant(a.startsAt, timeZone, lang)}</Text> },
                      { key: 'to', header: t('alert.col.expires'), width: 'minmax(150px, 1fr)', render: (a) => <Text role="caption" tone="secondary" mono>{formatMarketInstant(a.expiresAt, timeZone, lang)}</Text> },
                      { key: 'read', header: '', width: 'minmax(110px, 0.7fr)', render: (a) => <Badge tone={a.read === true ? 'neutral' : 'warning'}>{t(a.read === true ? 'alert.read' : 'alert.unread')}</Badge> },
                    ]}
                    rows={listed}
                    rowKey={(a) => a.id}
                    empty={<EmptyState title={t('alert.individual.none')} />}
                    minWidth={640}
                  />
                </div>
              )}
            </Stack></Panel>
          )}
        </>
      )}
    </>
  );
}
